// SwipeCarouselComponent.js
import React, { useEffect, useRef, useState } from "react";
import "./SwipeCarouselComponent.css";
import Swiper from "swiper";
import { Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import { CaretCircleLeft, CaretCircleRight } from "phosphor-react"; // Import more professional phosphor icons
// Replace react-feather with custom SVG components for better styling control
// This will allow us to style the SVG directly with CSS

const SwipeCarouselComponent = () => {
  const swiperRef = useRef(null);
  const [activeSlideIndex, setActiveSlideIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 700);
  const numSlides = 4;

  // Sample achievement data - replace with real data in production
  const achievements = [
    {
      id: 1,
      title: "Code First Girls",
      description: "Achieved a certificate in Python and Apps",
      imageUrl: "/images/cfg1.png",
    },
    {
      id: 2,
      title: "Snapchat Filter",
      description: "Created and published a public Snapchat filter",
      imageUrl: "/images/snapchat.png",
    },
    {
      id: 3,
      title: "Tiktok Management",
      description: "Managed a Tiktok account to reach over 3M views",
      imageUrl: "/images/tiktok.png",
    },
    {
      id: 4,
      title: "Photoshop",
      description: "A certified and licensed visual designer in Photoshop",
      imageUrl: "/images/photoshop.png",
    },
  ];

  const updateSlideTransforms = (swiper) => {
    if (!swiper || !swiper.slides) return;

    const rotateAmount = isMobile ? 5 : 7;
    const scaleAmount = isMobile ? 0.8 : 0.7;
    const translateYAmount = isMobile ? -15 : -30;

    swiper.slides.forEach((slide) => {
      const slideIndex = parseInt(
        slide.getAttribute("data-swiper-slide-index")
      );
      const image = slide.querySelector("img");

      const isPrev =
        slideIndex === swiper.realIndex - 1 ||
        (slideIndex === numSlides - 1 && swiper.realIndex === 0);
      const isNext =
        slideIndex === swiper.realIndex + 1 ||
        (slideIndex === 0 && swiper.realIndex === numSlides - 1);
      const isActive = slideIndex === swiper.realIndex;

      slide.removeAttribute("data-interactive"); // Reset first

      if (isActive) {
        slide.style.transform = `translateY(${translateYAmount}px) rotate(0deg) scale(1)`;
        slide.style.zIndex = 10;
        slide.style.visibility = "visible";
        image.style.opacity = 1;
        slide.style.cursor = "unset";
        slide.style.pointerEvents = "auto";
      } else if (isPrev) {
        slide.style.transform = `translateY(0px) rotate(-${rotateAmount}deg) scale(${scaleAmount})`;
        slide.style.zIndex = 9;
        slide.style.visibility = "visible";
        image.style.opacity = 1;
        slide.style.pointerEvents = "auto";
        slide.style.cursor = "pointer";
        slide.setAttribute("data-interactive", "true"); // Add interactive for prev
      } else if (isNext) {
        slide.style.transform = `translateY(0px) rotate(${rotateAmount}deg) scale(${scaleAmount})`;
        slide.style.zIndex = 9;
        slide.style.visibility = "visible";
        image.style.opacity = 1;
        slide.style.pointerEvents = "auto";
        slide.style.cursor = "pointer";
        slide.setAttribute("data-interactive", "true"); // Add interactive for next
      } else {
        slide.style.transform = `translateY(0px) rotate(0deg) scale(${
          scaleAmount * 0.8
        })`;
        image.style.opacity = 0;
        slide.style.pointerEvents = "none";
        slide.style.cursor = "normal";
        slide.style.visibility = "hidden";
        slide.style.zIndex = 8;
      }
    });
  };

  useEffect(() => {
    const initializeSwiper = () => {
      if (!swiperRef.current) {
        swiperRef.current = new Swiper(".swiper", {
          modules: [Navigation],
          slidesPerView: isMobile ? 1 : 3,
          // Adjust spacing for mobile to prevent overflow
          spaceBetween: isMobile ? 0 : -110,
          centeredSlides: true,
          loop: true,
          grabCursor: false, // Disable grab cursor
          loopedSlides: isMobile ? 1 : 3,
          resistanceRatio: 0,
          allowTouchMove: true, // Keep touch movement enabled
          navigation: {
            nextEl: ".swiper-button-next",
            prevEl: ".swiper-button-prev",
            enabled: true,
          },
          on: {
            slideChangeTransitionStart: (swiper) => {
              setActiveSlideIndex(swiper.realIndex);
              updateSlideTransforms(swiper); // Update transforms *before* the transition
            },
            init: (swiper) => {
              setActiveSlideIndex(swiper.realIndex);
              updateSlideTransforms(swiper);
            },
            transitionStart: (swiper) => {
              if (swiperRef.current) {
                swiperRef.current.navigation.init();
                swiperRef.current.navigation.update();
              }
            },
          },
        });
      } else {
        swiperRef.current.params.slidesPerView = isMobile ? 1 : 3;
        swiperRef.current.params.spaceBetween = isMobile ? 0 : -110;
        swiperRef.current.params.loopedSlides = isMobile ? 1 : 3;
        swiperRef.current.params.grabCursor = false; // Also disable here when updating
        swiperRef.current.params.allowTouchMove = true;
        swiperRef.current.navigation.nextEl = ".swiper-button-next";
        swiperRef.current.navigation.prevEl = ".swiper-button-prev";
        swiperRef.current.navigation.enabled = true;
        swiperRef.current.update();
      }
    };

    const handleResize = () => {
      const newIsMobile = window.innerWidth <= 700;
      if (newIsMobile !== isMobile) {
        setIsMobile(newIsMobile);
      }
    };

    initializeSwiper();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      if (swiperRef.current) {
        swiperRef.current.destroy();
        swiperRef.current = null;
      }
    };
  }, [isMobile]);

  useEffect(() => {
    if (swiperRef.current) {
      updateSlideTransforms(swiperRef.current);
    }
  }, [activeSlideIndex, isMobile]);

  const handleSlideClick = (index) => {
    if (swiperRef.current && index !== activeSlideIndex) {
      // Only change slide if clicking a *different* slide
      swiperRef.current.slideToLoop(index); // Use slideToLoop for correct looped behavior
    }
  };

  return (
    <div className="carousel">
      <h1>Recent Achievements</h1>
      <div className="swiper">
        <div className="swiper-wrapper">
          {achievements.map((achievement, index) => (
            <div
              className="swiper-slide"
              key={achievement.id}
              onClick={() => handleSlideClick(index)}
              data-interactive
              style={{ cursor: "none !important" }}
            >
              <div
                className={`single ${
                  index === activeSlideIndex ? "active" : ""
                }`}
              >
                <div className="image-container">
                  <img
                    src={achievement.imageUrl}
                    alt={achievement.title}
                    className={
                      index === activeSlideIndex ? "" : "inactive-image"
                    }
                  />
                  {index === activeSlideIndex && (
                    <span className="shimmer"></span>
                  )}
                  <div className="achievement-title">
                    <h3>{achievement.title}</h3>
                    <p>{achievement.description}</p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Professional Navigation Buttons */}
      <div className="navigation-container">
        <button
          className="swiper-button-prev nav-btn left"
          aria-label="Previous slide"
          data-interactive
        >
          <CaretCircleLeft size={40} weight="duotone" color="white" />
        </button>
        <button
          className="swiper-button-next nav-btn right"
          aria-label="Next slide"
          data-interactive
        >
          <CaretCircleRight size={40} weight="duotone" color="white" />
        </button>
      </div>
    </div>
  );
};

export default SwipeCarouselComponent;
